import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

const LOGO_URL = "/cgi-logo.png";
const CGI_WEBSITE = "https://www.commongroundinstitute.net";

export default function Login() {
  const { user, login, formatApiErrorDetail } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      nav("/");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      {/* LEFT: form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-clinical">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="flex flex-col items-start gap-3 mb-10">
            <a href={CGI_WEBSITE} target="_blank" rel="noopener noreferrer" aria-label="Visit the Common Ground Institute website">
              <img src={LOGO_URL} alt="Common Ground Institute" className="h-20 w-auto object-contain rounded-md" />
            </a>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 mt-4">
              Quiz: How Well Do You Manage Conflict?
            </h1>
            <p className="text-slate-600 leading-relaxed">
              Sign in to take the Capacity for Conflict Management Scale and view your personalised report.
            </p>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <PasswordInput
                    id="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    testIdPrefix="login-password"
                  />
                  <div className="text-right">
                    <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">
                      Forgot your password?
                    </Link>
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="login-submit-button"
                >
                  {submitting ? "Signing in…" : "Sign in"}
                </Button>
              </form>

              <p className="mt-6 text-sm text-slate-600 text-center">
                Don&apos;t have an account?{" "}
                <Link to="/register" className="text-primary font-medium hover:underline" data-testid="login-to-register-link">
                  Create one
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* RIGHT: warm gradient hero */}
      <div className="hidden md:block relative overflow-hidden bg-warm">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <blockquote className="max-w-md" data-testid="login-hero-quote">
            <p className="font-heading text-2xl text-slate-900 leading-snug italic">
              &ldquo;Conflict is not the opposite of connection — how we navigate it determines whether connection deepens or breaks.&rdquo;
            </p>
            <footer className="mt-4 text-sm text-slate-600">— Common Ground Institute</footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
